import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, ExternalLink, Download, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { getEvidence, getEvidenceById } from '../services/api';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

// ============================================
// EVIDENCE LIBRARY PAGE - Forbes Style (Voces)
// ============================================
export const EvidenceLibraryPage = () => {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getEvidence();
        setEvidence(data || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const types = ['all', 'documento', 'declaración', 'estadística', 'informe'];
  
  const filteredEvidence = evidence.filter(e => {
    const matchesSearch = !searchTerm || 
      e.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || e.document_type?.toLowerCase() === filter;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: SERIF_FONT }}>
            Voces
          </h1>
          <p className="text-gray-600 mt-2">Biblioteca de documentos, declaraciones y fuentes</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="border-b border-gray-100 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar documentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {/* Filters */}
            <div className="flex gap-2">
              {types.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    filter === t
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t === 'all' ? 'Todos' : t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Evidence Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvidence.map((item) => (
            <div
              key={item.evidence_id}
              className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-red-600" />
                </div>
                <Badge variant="outline" className="capitalize">
                  {item.document_type || 'documento'}
                </Badge>
              </div>
              <h3
                className="text-lg font-bold text-gray-900 hover:text-red-600 transition-colors mb-2 line-clamp-2"
                style={{ fontFamily: SERIF_FONT }}
              >
                {item.title}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                {item.description || 'Sin descripción disponible'}
              </p>
              <div className="flex items-center gap-2">
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver fuente
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredEvidence.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No se encontraron documentos</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// EVIDENCE DETAIL PAGE
// ============================================
export const EvidenceDetailPage = () => {
  const { id } = useParams();
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvidence = async () => {
      try {
        const data = await getEvidenceById(id);
        setEvidence(data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvidence();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  if (!evidence) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">Documento no encontrado</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/evidence" className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver a Voces
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-8">
        <Badge variant="outline" className="capitalize mb-4">
          {evidence.document_type || 'documento'}
        </Badge>
        <h1
          className="text-4xl font-bold text-gray-900 leading-tight mb-6"
          style={{ fontFamily: SERIF_FONT }}
        >
          {evidence.title}
        </h1>
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          {evidence.description}
        </p>

        {evidence.url && (
          <a
            href={evidence.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            Ver documento original
          </a>
        )}
      </article>
    </div>
  );
};

export default EvidenceLibraryPage;
